import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import AddUrlDialog from "./AddUrlDialog";

/* ---------------------------------------------------------
   Design tokens — kept in sync with Dashboard.jsx /
   SitemapGroups.jsx.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
  accentSoft: "#E3F1F1",
  success: "#2F9E44",
  successSoft: "#E9F7EC",
  danger: "#E0393B",
  dangerSoft: "#FCEAEA",
  warning: "#E8890C",
  warningSoft: "#FDF1E1",
};
const FONT_DISPLAY = "'Space Grotesk', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

const FONT_IMPORT = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
`;

// update / updateStatus now match the real routes
// (PUT /url/:id, PATCH /url/:id/status). delete-url is
// still a guess — confirm against your actual backend.
const ENDPOINTS = {
  list: "/api/v1/sitemap/urls",
  groups: "/api/v1/sitemap/groups",
  update: (id) => `/api/v1/sitemap/url/${id}`,
  updateStatus: (id) => `/api/v1/sitemap/url/${id}/status`,
  delete: (id) => `/api/v1/sitemap/delete-url/${id}`,
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

function useDebouncedValue(value, delayMs = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function SourceChip({ value }) {
  const isWordpress = value === "wordpress";
  return (
    <Chip
      size="small"
      label={isWordpress ? "WordPress" : "Node"}
      sx={{
        bgcolor: isWordpress ? COLOR.successSoft : COLOR.warningSoft,
        color: isWordpress ? COLOR.success : COLOR.warning,
        fontWeight: 500,
        fontSize: "12px",
      }}
    />
  );
}

export default function SitemapUrls() {
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, 400);

  const [groupFilter, setGroupFilter] = useState("");
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [editingUrl, setEditingUrl] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        const { data } = await axios.get(ENDPOINTS.groups);
        if (!cancelled) setGroups(data.data || []);
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.message || "Failed to load groups.");
        }
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };
    loadGroups();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchUrls = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(ENDPOINTS.list, {
        page: page + 1,
        limit: rowsPerPage,
        search,
        ...(groupFilter && { group_id: groupFilter }),
      });
      setRows(data.data || []);
      setTotalRows(data.pagination?.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch sitemap URLs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, groupFilter]);

  // Reset to the first page whenever the (debounced) search
  // term or the group filter changes, otherwise a stale page
  // number can land past the end of a now-smaller result set.
  useEffect(() => {
    setPage(0);
  }, [search, groupFilter]);

  const hasRows = rows.length > 0;

  const handleStatusChange = async (row) => {
    const nextEnabled = !row.enabled;
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, enabled: nextEnabled } : r))
    );

    try {
      await axios.patch(ENDPOINTS.updateStatus(row.id), { enabled: nextEnabled });
      toast.success(`URL ${nextEnabled ? "activated" : "deactivated"} successfully.`);
    } catch (error) {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, enabled: row.enabled } : r))
      );
      toast.error(error.response?.data?.message || "Failed to update status.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(ENDPOINTS.delete(deleteTarget.id));
      toast.success("URL deleted successfully.");
      setDeleteTarget(null);
      fetchUrls();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete URL.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => ["URL", "Source", "Type", "Priority", "Images", "Status", "Actions"],
    []
  );

  return (
    <>
      <style>{FONT_IMPORT}</style>

      <Box sx={{ fontFamily: "'Inter', sans-serif" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: "20px", color: COLOR.ink }}>
            Sitemap URLs
          </Typography>

          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingUrl(null);
              setAddOpen(true);
            }}
            sx={{
              bgcolor: COLOR.accent,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#095e6b" },
            }}
          >
            Add URL
          </Button>
        </Stack>

        <Paper
          elevation={0}
          sx={{ p: 2, mb: 2, borderRadius: "10px", border: `1px solid ${COLOR.line}` }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by URL…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: COLOR.inkFaint }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
            />

            <TextField
              select
              size="small"
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              disabled={loadingGroups}
              sx={{
                minWidth: { xs: "100%", sm: "240px" },
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (loadingGroups) return "Loading groups…";
                  if (!value) return "All groups";
                  const match = groups.find((g) => g.id === value);
                  return match ? match.name : "All groups";
                },
              }}
            >
              <MenuItem value="">All groups</MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name} ({g.url_count}/{g.chunk_size})
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{ borderRadius: "10px", border: `1px solid ${COLOR.line}`, overflow: "hidden" }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: COLOR.bg } }}>
                  {columns.map((col) => (
                    <TableCell
                      key={col}
                      align={col === "URL" ? "left" : "center"}
                      sx={{
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: COLOR.inkFaint,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        borderBottom: `1px solid ${COLOR.line}`,
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={26} sx={{ color: COLOR.accent }} />
                    </TableCell>
                  </TableRow>
                ) : !hasRows ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6, border: 0 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap="8px">
                        <LinkOffOutlinedIcon sx={{ fontSize: 28, color: COLOR.inkFaint }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: COLOR.ink }}>
                          {search || groupFilter ? "No URLs match your filters" : "No URLs yet"}
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: COLOR.inkMuted }}>
                          {search || groupFilter
                            ? "Try a different search term or group."
                            : "Add your first URL to get started."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover sx={{ "& td": { borderBottom: `1px solid ${COLOR.line}` } }}>
                      <TableCell sx={{ maxWidth: 420, wordBreak: "break-all", fontSize: "13.5px", color: COLOR.ink }}>
                        {row.url}
                      </TableCell>

                      <TableCell align="center">
                        <SourceChip value={row.source} />
                      </TableCell>

                      <TableCell align="center" sx={{ fontSize: "13px", color: COLOR.inkMuted, textTransform: "capitalize" }}>
                        {row.type || "—"}
                      </TableCell>

                      <TableCell align="center" sx={{ fontFamily: FONT_MONO, fontSize: "13px", color: COLOR.inkMuted }}>
                        {row.priority}
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          icon={<ImageOutlinedIcon sx={{ fontSize: 15 }} />}
                          label={row.images?.length || 0}
                          size="small"
                          sx={{ bgcolor: COLOR.bg, color: COLOR.inkMuted, fontFamily: FONT_MONO }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Switch
                          checked={!!row.enabled}
                          onChange={() => handleStatusChange(row)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: COLOR.accent },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              bgcolor: COLOR.accent,
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title="Edit URL">
                          <IconButton
                            size="small"
                            aria-label="Edit URL"
                            onClick={() => setEditingUrl(row)}
                            sx={{ color: COLOR.accent, mr: "4px" }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* WordPress-sourced URLs are synced from the
                            CMS automatically; manual deletion only
                            makes sense for custom/node-managed ones. */}
                        {/* {row.source === "node" && ( */}
                          <Tooltip title="Delete URL">
                            <IconButton
                              size="small"
                              aria-label="Delete URL"
                              onClick={() => setDeleteTarget(row)}
                              sx={{ color: COLOR.danger }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        {/* )} */}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ borderTop: `1px solid ${COLOR.line}` }}
          />
        </Paper>
      </Box>

      <AddUrlDialog
        open={addOpen || Boolean(editingUrl)}
        editingUrl={editingUrl}
        onClose={() => {
          setAddOpen(false);
          setEditingUrl(null);
        }}
        onSuccess={fetchUrls}
      />

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <WarningAmberOutlinedIcon sx={{ color: COLOR.danger }} />
          Delete URL
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: COLOR.ink, wordBreak: "break-all" }}>
            Are you sure you want to remove <b>{deleteTarget?.url}</b> from its
            sitemap? This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: "none", color: COLOR.inkMuted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={deleting}
            onClick={confirmDelete}
            sx={{
              bgcolor: COLOR.danger,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#c22f31" },
            }}
          >
            {deleting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}